import React, { useState, useEffect, useCallback } from 'react';
import { heroSlides } from '../data/mock';
import { ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const HeroSection = () => {
  const { isDark } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('next');

  const goToSlide = useCallback((index, dir = 'next') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % heroSlides.length;
    goToSlide(next, 'next');
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + heroSlides.length) % heroSlides.length;
    goToSlide(prev, 'prev');
  }, [currentSlide, goToSlide]);

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const slide = heroSlides[currentSlide];

  return (
    <section 
      id="hero" 
      aria-label="Hero carousel showcasing NEXMOVE services"
      className="min-h-screen flex items-center relative overflow-hidden"
    >
      {/* Background Images with transition */}
      {heroSlides.map((s, index) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={index !== currentSlide}
        >
          <img
            src={s.image}
            alt={`${s.badge} - ${s.headline} ${s.headlineAccent}: ${s.tagline}`}
            className="w-full h-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
          {/* Dark overlay for text readability */}
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-r from-[#0A0F0D]/95 via-[#0A0F0D]/85 to-[#0A0F0D]/70' 
              : 'bg-gradient-to-r from-black/80 via-black/60 to-black/40'
          }`} aria-hidden="true" />
        </div>
      ))}
      
      {/* Subtle pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] z-[1]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24 sm:py-32 w-full">
        <div className="max-w-4xl">
          {/* Badge - No star icon */}
          <div 
            className={`inline-flex items-center mb-6 sm:mb-8 ${
              isAnimating ? 'animate-slide-in' : ''
            }`}
            key={`badge-${currentSlide}`}
          >
            <span className="px-4 py-2 rounded-full border text-xs sm:text-sm font-semibold uppercase tracking-wider bg-white/10 backdrop-blur-sm border-white/20 text-white">
              {slide.badge}
            </span>
          </div>

          {/* Main headline with slide animation - removed overflow-hidden to show full letters */}
          <div className="mb-6 sm:mb-8">
            <h1 
              className={`font-display font-bold leading-[1.15] pb-2 transition-all duration-700 ease-out ${
                isAnimating 
                  ? direction === 'next' 
                    ? 'translate-y-8 opacity-0' 
                    : '-translate-y-8 opacity-0'
                  : 'translate-y-0 opacity-100'
              }`}
              style={{ fontSize: 'clamp(2rem, 6vw, 4.5rem)' }}
              key={`headline-${currentSlide}`}
            >
              <span className="text-white">
                {slide.headline}
              </span>
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7CFC00] via-[#98FB98] to-[#FFD700]">
                {slide.headlineAccent}
              </span>
            </h1>
          </div>

          {/* Tagline with fade animation */}
          <div>
            <p 
              className={`font-normal text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mb-8 sm:mb-12 transition-all duration-700 delay-100 ease-out text-gray-200 ${
                isAnimating 
                  ? 'translate-y-8 opacity-0' 
                  : 'translate-y-0 opacity-100'
              }`}
              key={`tagline-${currentSlide}`}
            >
              {slide.tagline}
            </p>
          </div>

          {/* CTAs */}
          <nav className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12" aria-label="Hero actions">
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="group bg-gradient-to-r from-[#006633] to-[#00873D] text-white font-semibold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:shadow-xl hover:shadow-green-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              data-testid="hero-contact-btn"
            >
              <span className="flex items-center justify-center gap-2">
                Start a Conversation
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </button>
            <button
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="group font-semibold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full border-2 transition-all duration-300 border-white/50 text-white hover:bg-white/10 hover:border-white backdrop-blur-sm"
              data-testid="hero-services-btn"
            >
              Explore Services
            </button>
          </nav>

          {/* Carousel Controls - Arrow style with dots */}
          <div className="flex items-center gap-3 sm:gap-4" role="group" aria-label="Carousel navigation">
            {/* Previous arrow */}
            <button
              onClick={prevSlide}
              disabled={isAnimating}
              className="p-2 sm:p-2.5 rounded-full transition-all duration-300 disabled:opacity-50 text-white/70 hover:text-white hover:bg-white/10"
              aria-label="Previous slide"
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>

            {/* Dot indicators */}
            <div className="flex items-center gap-1.5 sm:gap-2" role="tablist" aria-label="Slide indicators">
              {heroSlides.map((s, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index, index > currentSlide ? 'next' : 'prev')}
                  disabled={isAnimating}
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Slide ${index + 1}: ${s.badge}`}
                  className={`rounded-full transition-all duration-500 ${
                    index === currentSlide 
                      ? 'w-3 h-3 sm:w-3.5 sm:h-3.5 bg-gradient-to-r from-[#7CFC00] to-[#FFD700] shadow-md shadow-green-500/30' 
                      : 'w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>

            {/* Next arrow */}
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="p-2 sm:p-2.5 rounded-full transition-all duration-300 disabled:opacity-50 text-white/70 hover:text-white hover:bg-white/10"
              aria-label="Next slide"
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <button 
          onClick={scrollToAbout}
          className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 transition-colors animate-bounce text-white/50 hover:text-white"
          aria-label="Scroll down to About section"
        >
          <ArrowDown size={24} className="sm:w-7 sm:h-7" aria-hidden="true" />
        </button>
      </div>
      
      {/* Hidden announcement for screen readers */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Currently showing slide {currentSlide + 1} of {heroSlides.length}: {slide.badge}
      </div>
    </section>
  );
};

export default HeroSection;
